<template>
  <div>
    <PopoverLastTelemetryTimestamp :deviceId="deviceId" />
    <v-row no-gutters>
      <AqiMainValue
        :aqi="currentReading.aqi"
        :aqiType="mapAqiType"
        :measurementType="mapMeasurementType"
      />
      <v-spacer />
      <AqiAverages
        :deviceId="deviceId"
        :aqiType="mapAqiType"
        :measurementType="mapMeasurementType"
        ref="averages"
      />
    </v-row>
    <AqiPercentChanged :deviceId="deviceId" :aqiType="mapAqiType" ref="percentChanged" />
    <AqiDescription
      :aqi="currentReading.aqi"
      :aqiType="mapAqiType"
      :measurementType="mapMeasurementType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
// Content components
import PopoverLastTelemetryTimestamp from '../../header/popoverLastTelemetryTimestamp.vue'
import AqiMainValue from './aqiMainValue.vue'
import AqiAverages from './aqiAverages.vue'
import AqiPercentChanged from './aqiPercentChanged.vue'
import AqiDescription from './aqiDescription.vue'

import { getMeasurementValueByName } from '@/components/devicePopover/utils'
import { activeLastDay } from '@/services/device-status'
import { MeasurementType, measurementTypesKey } from '@/services/device-measurements'
import { aqiTypes } from '@/store/map/utils'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'
import { DEVICE_GETTERS } from '@/store/devices'
import type { GetterByDeviceId } from '@/components/devicePopover/types'
import type { SensorReadings } from '@/services/device-service/devices'

interface CurrentReading {
  aqi: number | string
  raw: number | string
}

export default defineComponent({
  name: 'AQIMeasurement',
  components: {
    PopoverLastTelemetryTimestamp,
    AqiMainValue,
    AqiAverages,
    AqiPercentChanged,
    AqiDescription
  },
  props: {
    deviceId: { type: String, default: '' }
  },
  computed: {
    mapAqiType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`] as number
    },
    mapMeasurementType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`] as number
    },
    lastTelemetryTimestamp(): string | null {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string | null>
      return getter(this.deviceId)
    },
    sensorReadings(): SensorReadings {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SENSOR_READINGS_BY_DEVICE_ID}`
      ] as GetterByDeviceId<SensorReadings>
      return getter(this.deviceId)
    },
    currentReading(): CurrentReading {
      const DEFAULT_READING_VALUE = this.$t('map.popover.valueBlank')
      const reading: CurrentReading = { aqi: DEFAULT_READING_VALUE, raw: DEFAULT_READING_VALUE }
      if (activeLastDay(this.lastTelemetryTimestamp ?? '')) {
        const measType = this.mapMeasurementType
        const isIndia = this.mapAqiType === aqiTypes.in
        let aqiPmKey: number | undefined
        let rawPmKey: number | undefined
        if (measType === MeasurementType.Pm25) {
          aqiPmKey = isIndia ? MeasurementType.Pm25IndiaAqi : MeasurementType.Pm25
          rawPmKey = MeasurementType.Pm25Raw
        }
        if (measType === MeasurementType.Pm10) {
          aqiPmKey = isIndia ? MeasurementType.Pm10IndiaAqi : MeasurementType.Pm10
          rawPmKey = MeasurementType.Pm10Raw
        }

        if (aqiPmKey !== undefined && rawPmKey !== undefined) {
          reading.aqi = getMeasurementValueByName(
            this.sensorReadings,
            measurementTypesKey[aqiPmKey as MeasurementType],
            undefined
          )
          reading.raw = getMeasurementValueByName(
            this.sensorReadings,
            measurementTypesKey[rawPmKey as MeasurementType],
            undefined
          )
        }
      }
      return reading
    }
  },
  methods: {
    refresh(): void {
      const averages = this.$refs.averages as InstanceType<typeof AqiAverages>
      const percentChanged = this.$refs.percentChanged as InstanceType<typeof AqiPercentChanged>
      averages?.loadData?.()
      percentChanged?.loadData?.()
    }
  }
})
</script>
